import React, { useEffect, useState, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import styles from "./OrderDetails.module.css";

import Navbar from "components/Navbar";
import BottomNav from "components/BottomNav";
import ProductImage from "Shared/ProductImage";

import ReceiptIcon from "../../assets/Reciept_Icon.svg";
import BackArrow from "../../assets/Back_Arrow.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getCardBrandIcon } from "../../utils/cardBrandIcon";
import { useOrder } from "../../hooks/useOrders";
import { useDeleteOrder, useOrderSupport } from "../../hooks/useOrderActions";
import { useCart, useAddToCart, getCartItemCount } from "../../hooks/useCart";
import { usePaymentMethods } from "../../hooks/usePaymentMethods";
import { SelectButton } from "components/SelectButton";
import { trackProductAdded } from "../../utils/analytics";
import {
  formatRetailPrice,
  RETAIL_PRICE_LABEL,
} from "../../utils/formatRetailPrice";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const day = date.getDate();
  const year = date.getFullYear();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";
  return `${month} ${day}${suffix}, ${year}`;
};

export default function OrderDetails({
  initialHelpOpen = false,
  onItemSelectionChange,
}) {
  const { orderId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const isNewOrder =
    new URLSearchParams(location.search).get("isNew") === "true";

  const { data: order, isLoading } = useOrder(orderId, { isNew: isNewOrder });
  const addToCart = useAddToCart();
  const deleteOrder = useDeleteOrder();
  const orderSupport = useOrderSupport();
  const { data: paymentCards = [] } = usePaymentMethods();
  const { data: cartData } = useCart();
  const cartCount = getCartItemCount(cartData);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 797);
  const [isHelpOpen, setIsHelpOpen] = useState(initialHelpOpen);
  const [selectedIssue, setSelectedIssue] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const bottomRef = useRef(null);

  const checkoutContext = location.state?.checkoutContext ?? null;

  const sanitizePlaceholder = (value) => {
    const normalized = String(value ?? "").trim();
    if (!normalized) return null;
    if (normalized.toUpperCase() === "N/A") return null;
    if (normalized === "----") return null;
    return normalized;
  };

  const issueOptions = [
    "Product damaged, shipping box OK",
    "Product and shipping box both damaged",
    "Received extra item I didn't buy",
    "Wrong item was sent",
    "Bought by mistake",
    "Item defective",
    "Missing or broken parts",
    "Other",
  ];

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 797);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (onItemSelectionChange) {
      onItemSelectionChange(selectedItems);
    }
  }, [selectedItems]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isHelpOpen) {
      setTimeout(() => {
        if (bottomRef.current) {
          bottomRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        } else {
          window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 0);
    }
  }, [isHelpOpen]);

  const primaryCard =
    paymentCards.find((card) => card?.isPrimary) ?? paymentCards[0] ?? null;
  const activeCard = primaryCard;

  const menuItems = [
    { label: "WWG Store", href: "/wwg-store" },
    { label: "My Lists", href: "/my-lists" },
    { label: "My Orders", href: "/my-orders" },
    { label: "Cart", href: "/cart" },
  ];

  const handleTabClick = (item) => {
    history.push(item.href);
  };

  if (isLoading) {
    return null;
  }

  if (!order) {
    return (
      <div className={styles.pageWrapper}>
        {!isMobile && (
          <Navbar
            onClick={() => {}}
            isHome={true}
            menuItems={menuItems}
            activeTab="My Orders"
            onTabClick={handleTabClick}
            cartCount={0}
          />
        )}
        <div
          className={styles.backNav}
          onClick={() => history.push("/my-orders")}
        >
          <img src={BackArrow} alt="Back" className={styles.backIcon} />
          <span className={styles.backLabel}>My Orders</span>
        </div>
        <h2>Order Not Found</h2>
        {isMobile && (
          <>
            <div className={styles.bottomSpacer} />
            <BottomNav cartCount={0} activePath="/my-orders" />
          </>
        )}
      </div>
    );
  }

  const actionOrderId = String(
    order.orderId ?? order.transactionId ?? order.id ?? orderId
  );

  const billingAddress = order?.billingAddress ?? {};
  const fallbackAddress = checkoutContext?.shippingAddress ?? {};

  const addressLine1 =
    sanitizePlaceholder(billingAddress?.street) ??
    sanitizePlaceholder(fallbackAddress?.address1) ??
    sanitizePlaceholder(fallbackAddress?.address2) ??
    "-";

  const cityValue =
    sanitizePlaceholder(billingAddress?.city) ??
    sanitizePlaceholder(fallbackAddress?.city);
  const stateValue =
    sanitizePlaceholder(billingAddress?.state) ??
    sanitizePlaceholder(fallbackAddress?.state);
  const zipValue =
    sanitizePlaceholder(billingAddress?.zip) ??
    sanitizePlaceholder(fallbackAddress?.zip);

  const cityStateZipParts = [cityValue, stateValue, zipValue].filter(Boolean);
  const addressLine2 =
    cityStateZipParts.length > 0 ? cityStateZipParts.join(" ") : "-";

  const orderCardType =
    sanitizePlaceholder(order?.paymentMethod?.type) ??
    sanitizePlaceholder(checkoutContext?.paymentCard?.cardType) ??
    sanitizePlaceholder(activeCard?.cardType) ??
    "Card";
  const orderCardLast4 =
    sanitizePlaceholder(order?.paymentMethod?.last4) ??
    sanitizePlaceholder(checkoutContext?.paymentCard?.last4) ??
    sanitizePlaceholder(activeCard?.lastFour) ??
    sanitizePlaceholder(activeCard?.last4);

  const renderMeta = (it) => {
    if (it?.author) {
      return (
        <p className={styles.rowMeta}>
          <span className={styles.authorLabel}>Author:</span>{" "}
          <span className={styles.metaDark}>{it.author}</span>
        </p>
      );
    }

    return null;
  };

  const renderReceipt = () => (
    <div className={styles.receiptContainer}>
      <div className={styles.receiptHeader}>
        <img
          src={ReceiptIcon}
          alt="Receipt Icon"
          className={styles.receiptIcon}
        />
        <span className={styles.receiptStoreTitle}>
          WWG Store <br /> Receipt
        </span>
      </div>
      <div className={styles.receiptDivider}></div>

      <div className={styles.receiptSummary}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Purchased On:</span>
          <span className={styles.summaryValueBold}>
            {formatDate(order.date)}
          </span>
        </div>
        <div className={`${styles.summaryRow} ${styles.summaryRowRight}`}>
          <span className={styles.summaryLabel}>Order Number:</span>
          <span className={styles.summaryValueBold}>{order.id}</span>
        </div>
      </div>

      <div className={styles.receiptDividerBold}></div>

      <div className={styles.receiptItems}>
        {order.items.map((item) => (
          <div key={item.stockId} className={styles.receiptItem}>
            <span className={styles.receiptLabel}>{item.title}</span>
            <span className={styles.receiptValue}>
              ${item.memberPrice ? item.memberPrice.toFixed(2) : "0.00"}
            </span>
          </div>
        ))}

        <div className={styles.receiptDivider}></div>

        <div className={styles.subtotalRow}>
          <span className={styles.subtotalLabel}>Subtotal</span>
          <span className={styles.receiptValue}>
            ${order.subtotal.toFixed(2)}
          </span>
        </div>
        <div className={styles.taxRow}>
          <span className={styles.taxLabel}>Shipping Amount</span>
          <span className={styles.receiptValue}>
            ${order.shipping.toFixed(2)}
          </span>
        </div>
        <div className={styles.taxRow}>
          <span className={styles.taxLabel}>Tax</span>
          <span className={styles.receiptValue}>${order.tax.toFixed(2)}</span>
        </div>
        <div className={styles.totalRow}>
          <span className={styles.totalLabel}>Order Total</span>
          <span className={styles.summaryValueBold}>
            ${order.total.toFixed(2)}
          </span>
        </div>
        <div className={styles.receiptDivider}></div>
      </div>

      <div className={styles.billingSection}>
        <div className={styles.billingInfo}>
          <span className={styles.billingLabel}>Shipping Address</span>
          <div className={styles.billingAddress}>
            <span>{addressLine1}</span>
            <span>{addressLine2}</span>
          </div>
        </div>
      </div>

      <div className={styles.receiptDivider}></div>

      <div className={styles.paymentSection}>
        <span className={styles.paymentLabel}>Pay With</span>
        <div className={styles.paymentInfo}>
          <div className={styles.cardInfo}>
            <FontAwesomeIcon
              icon={getCardBrandIcon(orderCardType)}
              title={orderCardType}
              className={styles.cardIcon}
            />
            <span className={styles.cardNumber}>
              {orderCardLast4 ? `*${orderCardLast4}` : "Card on file"}
            </span>
          </div>
        </div>
        <div className={styles.receiptDivider}></div>
      </div>

      {!order.isPreOrder && (
        <>
          <div className={styles.helpSection}>
            <div className={styles.helpTitle}>
              {isHelpOpen ? "Items with issues?" : "Need help with your order?"}
            </div>

            {!isHelpOpen ? (
              <div className={styles.helpRow}>
                <span className={styles.helpLabel}>
                  Refund or Replace Items?
                </span>
                <button
                  className={styles.editButton}
                  onClick={() => setIsHelpOpen(true)}
                >
                  Help
                </button>
              </div>
            ) : (
              <div className={styles.issuesSection}>
                <div className={styles.sectionDivider} />

                <div className={styles.helpItemsList}>
                  {order.items.map((it) => (
                    <div key={it.stockId} className={styles.helpItemRow}>
                      <ProductImage
                        src={it.imageUrl}
                        alt={it.title}
                        className={styles.rowImage}
                      />
                      <div className={styles.rowMiddle}>
                        <h4 className={styles.rowTitle}>{it.title}</h4>
                        <p className={styles.rowMeta}>
                          Premier Price:{" "}
                          <span className={styles.metaDark}>
                            $
                            {it.memberPrice
                              ? it.memberPrice.toFixed(2)
                              : "0.00"}
                          </span>
                        </p>
                        {formatRetailPrice(it.retailPrice) && (
                          <p className={styles.rowMeta}>
                            {RETAIL_PRICE_LABEL}:{" "}
                            <span className={styles.metaDark}>
                              {formatRetailPrice(it.retailPrice)}
                            </span>
                          </p>
                        )}
                        {(it.color || it.size) && (
                          <div className={styles.itemMeta}>
                            {it.color && (
                              <>
                                <span className={styles.itemMetaLabel}>
                                  Color:
                                </span>
                                <span className={styles.itemMetaValue}>
                                  {it.color}
                                </span>
                              </>
                            )}
                            {it.size && (
                              <>
                                <span className={styles.itemMetaLabel}>
                                  &nbsp;Size:
                                </span>
                                <span className={styles.itemMetaValue}>
                                  {it.size}
                                </span>
                              </>
                            )}
                          </div>
                        )}
                        {it.author && (
                          <p className={styles.rowMeta}>
                            <span className={styles.authorLabel}>Author:</span>{" "}
                            <span className={styles.metaDark}>{it.author}</span>
                          </p>
                        )}
                        <div className={styles.selectBtnWrapper}>
                          <SelectButton
                            selected={selectedItems.includes(it.stockId)}
                            onClick={() =>
                              setSelectedItems((prev) =>
                                prev.includes(it.stockId)
                                  ? prev.filter((id) => id !== it.stockId)
                                  : [...prev, it.stockId]
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className={styles.rowRight}>
                        Qty:
                        <span className={styles.qtyValue}>
                          {" "}
                          {it.quantity ?? 1}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.sectionDivider} />

                <div className={styles.issuesTitle}>What are the issues?</div>
                <div className={styles.radioList}>
                  {issueOptions.map((opt) => (
                    <label key={opt} className={styles.radioItem}>
                      <input
                        type="radio"
                        name="order-issue"
                        value={opt}
                        checked={selectedIssue === opt}
                        onChange={() => setSelectedIssue(opt)}
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
                <div className={styles.finishText}>
                  Finish by contacting support.
                </div>
                <div className={styles.actionsRow}>
                  <button
                    className={styles.contactButton}
                    onClick={() =>
                      orderSupport.mutate({
                        orderId: actionOrderId,
                        issue: selectedIssue,
                      })
                    }
                  >
                    Contact Support
                  </button>
                  <button
                    className={styles.cancelTicketButton}
                    onClick={() => {
                      setIsHelpOpen(false);
                      setSelectedIssue("");
                      setSelectedItems([]);
                    }}
                  >
                    Cancel Ticket
                  </button>
                </div>
                <div ref={bottomRef} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderCompletedOrder = () => (
    <>
      <div className={styles.trackingRow}>
        Tracking #:
        <span className={styles.trackingNumber}> {order.tracking}</span>
      </div>

      <div className={styles.sectionDivider} />

      <h3 className={styles.itemsHeader}>Items in this order</h3>
      <div className={styles.sectionDivider} />
      <div className={styles.itemsList}>
        {order.items.map((it) => (
          <div key={it.stockId} className={styles.itemRow}>
            <ProductImage
              src={it.imageUrl}
              alt={it.title}
              className={styles.rowImage}
            />

            <div className={styles.rowMiddle}>
              <h4 className={styles.rowTitle}>{it.title}</h4>

              <p className={styles.rowMeta}>
                Premier Price:{" "}
                <span className={styles.metaDark}>
                  ${it.memberPrice ? it.memberPrice.toFixed(2) : "0.00"}
                </span>
              </p>

              {formatRetailPrice(it.retailPrice) && (
                <p className={styles.rowMeta}>
                  {RETAIL_PRICE_LABEL}:{" "}
                  <span className={styles.metaDark}>
                    {formatRetailPrice(it.retailPrice)}
                  </span>
                </p>
              )}

              {(it.color || it.size) && (
                <div className={styles.itemMeta}>
                  {it.color && (
                    <>
                      <span className={styles.itemMetaLabel}>Color:</span>
                      <span className={styles.itemMetaValue}>{it.color}</span>
                    </>
                  )}
                  {it.size && (
                    <>
                      <span className={styles.itemMetaLabel}>&nbsp;Size:</span>
                      <span className={styles.itemMetaValue}>{it.size}</span>
                    </>
                  )}
                </div>
              )}

              {renderMeta(it)}

              <button
                className={styles.buyAgainBtn}
                onClick={() =>
                  addToCart.mutate(
                    {
                      productId: it.productId,
                      variationId: it.variationId,
                      variantId: it.variantId,
                      quantity: it.quantity ?? 1,
                    },
                    {
                      onSuccess: () => {
                        trackProductAdded(it, {
                          quantity: it.quantity ?? 1,
                          reOrder: true,
                        });
                        history.push("/cart");
                      },
                    }
                  )
                }
              >
                Buy Again
              </button>
            </div>

            <div className={styles.rowRight}>
              Qty:<span className={styles.qtyValue}> {it.quantity ?? 1}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.sectionDivider} />

      <h2 className={styles.receiptTitle}>Receipt</h2>
      <div className={styles.sectionDivider} />

      {renderReceipt()}
    </>
  );

  const renderPreOrder = () => (
    <>
      <div className={styles.trackingRow}>
        Tracking #:
        <span className={styles.trackingNumber}> {order.tracking}</span>
      </div>

      <div className={styles.available}>Available 1.13.24</div>
      <div className={styles.sectionDivider}></div>
      <h2 className={styles.sectionTitle}>Pre-Order Item</h2>
      <div className={styles.sectionDivider}></div>

      <h3 className={styles.shippingTitle}>Shipping Immediately</h3>
      <div className={styles.itemCard}>
        <img
          src={order.items[0].imageUrl}
          alt={order.items[0].title}
          className={styles.itemImage}
        />
        <div className={styles.itemInfo}>
          <h3 className={styles.itemTitle}>{order.items[0].title}</h3>
          <p className={styles.itemPrice}>
            Premier Price:{" "}
            <span>
              $
              {order.items[0].memberPrice
                ? order.items[0].memberPrice.toFixed(2)
                : "0.00"}
            </span>
          </p>
          {formatRetailPrice(order.items[0].retailPrice) && (
            <p className={styles.itemPrice}>
              {RETAIL_PRICE_LABEL}:{" "}
              <span>{formatRetailPrice(order.items[0].retailPrice)}</span>
            </p>
          )}
          {order.items[0].author && (
            <div className={styles.itemAuthor}>
              <span className={styles.itemAuthorLabel}>Author:</span>
              <span className={styles.itemAuthorName}>
                {order.items[0].author}
              </span>
            </div>
          )}

          <div className={styles.actionButtons}>
            <button
              className={styles.cancelButton}
              onClick={() => deleteOrder.mutate(actionOrderId)}
            >
              Cancel Pre-order
            </button>
          </div>
        </div>
      </div>

      <div className={styles.sectionDivider} />

      <h2 className={styles.receiptTitle}>Receipt</h2>
      <div className={styles.sectionDivider} />

      {renderReceipt()}
    </>
  );

  return (
    <div className={styles.pageWrapper}>
      {!isMobile && (
        <Navbar
          onClick={() => {}}
          isHome={true}
          menuItems={menuItems}
          activeTab="My Orders"
          onTabClick={handleTabClick}
          cartCount={cartCount}
        />
      )}

      <div
        className={styles.backNav}
        onClick={() => history.push("/my-orders")}
      >
        <img src={BackArrow} alt="Back" className={styles.backIcon} />
        <span className={styles.backLabel}>My Orders</span>
      </div>

      {order.isPreOrder ? renderPreOrder() : renderCompletedOrder()}

      {isMobile && (
        <>
          <div className={styles.bottomSpacer} />
          <BottomNav cartCount={cartCount} activePath="/my-orders" />
        </>
      )}
    </div>
  );
}
